import { useMemo } from "react";
import {
  CfarExercisePayoutEnum,
  HotelAncillaryEnum,
  type CfarContract,
  type HotelAncillary,
} from "@b2bportal/lodging-api";
import { useI18nContext, DateTimeFormatStyle } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { formatDateTime } from "@hopper-i18n/formatter";
import dayjs from "dayjs";

export const findCfarContract = (
  ancillaries: HotelAncillary[] | undefined
): CfarContract | undefined =>
  ancillaries?.find(
    (a): a is CfarContract =>
      a.HotelAncillary === HotelAncillaryEnum.CfarContract
  );

export const hasCfarContract = (
  ancillaries: HotelAncillary[] | undefined
): boolean => findCfarContract(ancillaries) != null;

/**
 * Formats a CFAR expiry timestamp as a month-and-day date, with no time of day.
 *
 * RBC CFAR always expires at check-in, but the upstream offer never carries a
 * real check-in time, so the raw timestamp holds a placeholder clock time that
 * would contradict the check-in time shown beside it. Only the date is truthful.
 *
 * @param cfarExpiryTimestamp expiry with an optional IANA zone suffix,
 *   e.g. `"2025-03-27T20:00-04:00[America/Toronto]"`, or a bare `"2025-03-27"`
 * @param language locale tag used to format the date, e.g. `"en-CA"`
 * @returns the localised date, e.g. `"Mar 27"`
 */
export const formatCfarServiceExpiryDate = (
  cfarExpiryTimestamp: string,
  language: string
): string =>
  formatDateTime(
    // dayjs, not `new Date`: a date-only string would otherwise parse as
    // midnight UTC and render as the previous day in western timezones.
    dayjs(removeTimezone(cfarExpiryTimestamp)).toDate(),
    language,
    DateTimeFormatStyle.Custom({ month: "short", day: "numeric" })
  );

export const useCfarRefundAmount = (
  cfarContract: CfarContract | undefined | null
): string | null => {
  const { t, formatFiatCurrency, formatNumber } = useI18nContext();

  return useMemo(() => {
    if (cfarContract?.payoutBreakdown != null) {
      return cfarContract.payoutBreakdown.payouts
        .filter(
          (payout) =>
            payout.CfarExercisePayout === CfarExercisePayoutEnum.Card ||
            payout.CfarExercisePayout === CfarExercisePayoutEnum.LoyaltyPoints
        )
        .sort((a, b) => {
          const aIsLoyalty =
            a.CfarExercisePayout === CfarExercisePayoutEnum.LoyaltyPoints;
          const bIsLoyalty =
            b.CfarExercisePayout === CfarExercisePayoutEnum.LoyaltyPoints;
          return bIsLoyalty ? 1 : aIsLoyalty ? -1 : 0;
        })
        .map((payout) => {
          if (
            payout.CfarExercisePayout === CfarExercisePayoutEnum.LoyaltyPoints
          ) {
            return t("core-hotels-cfar-exercise.loyaltyPointsPayout", {
              points: formatNumber(payout.pointsAmount),
            });
          }

          if (payout.CfarExercisePayout === CfarExercisePayoutEnum.Card) {
            return t("core-hotels-cfar-exercise.cardPayout", {
              amount: formatFiatCurrency(payout.amount),
            });
          }

          return null;
        })
        .filter(Boolean)
        .join(" + ");
    }

    if (cfarContract?.coverage != null) {
      return formatFiatCurrency(cfarContract.coverage.fiat);
    }

    return null;
  }, [cfarContract, formatFiatCurrency, formatNumber, t]);
};
